import { useState } from "react";
import { ExternalLink, Music2, Hash, Camera, Ghost, Video, Users, ChevronDown, ChevronUp, Sparkles } from "lucide-react";

// Per-room theme + content configuration
const ROOM_THEMES = {
  "tiktok-room": {
    title: "🎵 THE TIK TOK ROOM",
    titleBarStyle: "bg-gradient-to-r from-black via-fuchsia-600 to-cyan-400 text-white",
    panelBg: "bg-gradient-to-b from-black via-zinc-900 to-black text-white",
    accent: "bg-gradient-to-r from-fuchsia-500 to-cyan-400 text-black",
    chipColor: "bg-zinc-800 hover:bg-zinc-700 text-cyan-300 border border-fuchsia-500",
    icon: Music2,
    quickLinks: [
      { name: "TikTok", url: "https://www.tiktok.com", color: "bg-black border border-fuchsia-500" },
      { name: "Trending Sounds", url: "https://www.tiktok.com/discover", color: "bg-fuchsia-600" },
      { name: "Creator Tools", url: "https://www.tiktok.com/creators", color: "bg-cyan-600" },
    ],
    chips: [
      "♬ vibing to this sound",
      "#fyp",
      "#foryoupage",
      "POV: 🎬",
      "Tell me you... without telling me you...",
      "Wait for it... 🤯",
      "It's giving main character energy ✨",
      "Slay 💅",
      "Bestie no 💀",
      "Rate this transition 🎞️",
    ],
    tagline: "♬ TRENDING ON THE FYP ♬",
  },
  "snapchat-room": {
    title: "👻 THE SNAP CHAT ROOM",
    titleBarStyle: "bg-[#FFFC00] text-black border-b-2 border-black",
    panelBg: "bg-gradient-to-b from-[#FFFC00] via-yellow-100 to-white text-black",
    accent: "bg-black text-[#FFFC00]",
    chipColor: "bg-white hover:bg-yellow-50 text-black border-2 border-black shadow-[2px_2px_0_#000]",
    icon: Ghost,
    quickLinks: [
      { name: "Snapchat", url: "https://www.snapchat.com", color: "bg-[#FFFC00] text-black border-2 border-black" },
      { name: "Snap Stories", url: "https://www.snapchat.com/discover", color: "bg-black text-[#FFFC00]" },
      { name: "Lens Studio", url: "https://lensstudio.snapchat.com", color: "bg-pink-500" },
    ],
    chips: [
      "👻 Streaks anyone?",
      "🔥 30-day streak unlocked!",
      "Send a snap back 📸",
      "Just opened your snap 👀",
      "Story time! 📖",
      "Add me on Snap 👻",
      "Bitmoji vibes only",
      "Sus 🤨",
      "Screenshot? I see you 📸",
      "Best Friends Forever 💛",
    ],
    tagline: "👻 SNAP, STORY, STREAK 👻",
  },
  "instagram-room": {
    title: "📸 THE INSTAGRAM ROOM",
    titleBarStyle: "bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-600 text-white",
    panelBg: "bg-gradient-to-br from-yellow-50 via-pink-50 to-purple-100 text-zinc-900",
    accent: "bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-600 text-white",
    chipColor: "bg-white hover:bg-pink-50 text-pink-700 border border-pink-300 shadow-sm",
    icon: Camera,
    quickLinks: [
      { name: "Instagram", url: "https://www.instagram.com", color: "bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-600" },
      { name: "Reels", url: "https://www.instagram.com/reels", color: "bg-pink-600" },
      { name: "Explore", url: "https://www.instagram.com/explore", color: "bg-purple-600" },
    ],
    chips: [
      "📸 Just posted, check my story!",
      "#instagood",
      "#photooftheday",
      "#reelsofinstagram",
      "Slide 1 of 10 ➡️",
      "Tag a friend 👇",
      "Saving this one 🔖",
      "Going live in 5 🔴",
      "Aesthetic 🌸",
      "Caption this 💭",
    ],
    tagline: "📸 GRAM IT, REEL IT, STORY IT 📸",
  },
  "content-creator-hangout": {
    title: "🎬 THE CONTENT CREATOR HANG OUT",
    titleBarStyle: "bg-gradient-to-r from-red-600 via-purple-600 to-indigo-700 text-white",
    panelBg: "bg-gradient-to-b from-zinc-900 via-purple-950 to-zinc-900 text-white",
    accent: "bg-gradient-to-r from-red-500 to-yellow-400 text-black",
    chipColor: "bg-zinc-800 hover:bg-zinc-700 text-yellow-300 border border-red-500",
    icon: Video,
    quickLinks: [
      { name: "YouTube Studio", url: "https://studio.youtube.com", color: "bg-red-600" },
      { name: "TikTok Creators", url: "https://www.tiktok.com/creators", color: "bg-fuchsia-600" },
      { name: "Twitch", url: "https://www.twitch.tv", color: "bg-purple-700" },
      { name: "Patreon", url: "https://www.patreon.com", color: "bg-orange-600" },
      { name: "Kick", url: "https://www.kick.com", color: "bg-green-600" },
      { name: "Buy Me a Coffee", url: "https://www.buymeacoffee.com", color: "bg-yellow-600 text-black" },
    ],
    chips: [
      "🎬 Smash that subscribe button!",
      "💰 Monetization tips?",
      "📊 What's your CTR?",
      "🔔 Hit the bell, fam",
      "Editing in Premiere or DaVinci?",
      "Algorithm hates me today 😩",
      "Going viral? 🚀",
      "Sponsorship inquiries DM",
      "Collab anyone? 🤝",
      "Burnout is real 😮‍💨",
    ],
    tagline: "🎬 CREATE • POST • GROW 🎬",
  },
  "facebook-room": {
    title: "📘 FACEBOOK ROOM",
    titleBarStyle: "bg-[#1877F2] text-white",
    panelBg: "bg-gradient-to-b from-[#F0F2F5] to-white text-zinc-900",
    accent: "bg-[#1877F2] text-white",
    chipColor: "bg-white hover:bg-blue-50 text-[#1877F2] border border-blue-300 shadow-sm",
    icon: Users,
    quickLinks: [
      { name: "Facebook", url: "https://www.facebook.com", color: "bg-[#1877F2]" },
      { name: "Marketplace", url: "https://www.facebook.com/marketplace", color: "bg-blue-600" },
      { name: "Watch", url: "https://www.facebook.com/watch", color: "bg-indigo-600" },
      { name: "Groups", url: "https://www.facebook.com/groups", color: "bg-blue-500" },
    ],
    chips: [
      "👍 Like and share!",
      "Friend request sent 🤝",
      "Throwback Thursday! 📅",
      "Check-in: 📍",
      "Marketplace find 🛒",
      "Going to an event 🎉",
      "Family pics 👨‍👩‍👧‍👦",
      "Memories from this day 🕰️",
      "It's complicated 💔",
      "Poke! 👉",
    ],
    tagline: "📘 CONNECT WITH FRIENDS & FAMILY 📘",
  },
  "onlyfans-room": {
    title: "🌶️ THE ONLYFANS ROOM",
    titleBarStyle: "bg-gradient-to-r from-[#00AFF0] to-cyan-500 text-white",
    panelBg: "bg-gradient-to-b from-cyan-50 via-white to-pink-50 text-zinc-900",
    accent: "bg-[#00AFF0] text-white",
    chipColor: "bg-white hover:bg-cyan-50 text-[#00AFF0] border border-[#00AFF0] shadow-sm",
    icon: Sparkles,
    quickLinks: [
      { name: "OnlyFans", url: "https://onlyfans.com", color: "bg-[#00AFF0]" },
      { name: "Creator Hub", url: "https://onlyfans.com/blog", color: "bg-cyan-600" },
    ],
    chips: [
      "🔥 New post dropped",
      "Tip jar open 💰",
      "DMs are open 💌",
      "Subscribe for exclusive content ✨",
      "Custom requests 📸",
      "Sub goal hit! 🎉",
      "Promo code: ____",
      "Top creator 👑",
      "Loyalty rewards 💎",
      "PPV inbox 📥",
    ],
    tagline: "🌶️ 18+ CREATORS & FANS 🌶️",
  },
  "telegram-room": {
    title: "✈️ THE TELEGRAM ROOM",
    titleBarStyle: "bg-gradient-to-r from-[#0088CC] to-sky-500 text-white",
    panelBg: "bg-gradient-to-b from-sky-50 via-white to-blue-50 text-zinc-900",
    accent: "bg-[#0088CC] text-white",
    chipColor: "bg-white hover:bg-sky-50 text-[#0088CC] border border-sky-300 shadow-sm",
    icon: Hash,
    quickLinks: [
      { name: "Telegram Web", url: "https://web.telegram.org", color: "bg-[#0088CC]" },
      { name: "Discover Channels", url: "https://telegram.org/apps", color: "bg-sky-600" },
      { name: "Stickers", url: "https://t.me/stickers", color: "bg-blue-500" },
    ],
    chips: [
      "✈️ Drop your @username",
      "Join my channel: t.me/____",
      "Secret chat? 🔒",
      "Forwarded 📤",
      "Sticker pack 🎨",
      "Pin this 📌",
      "Bot @____",
      "Reacting with 🔥",
      "Voice message inbound 🎤",
      "Disappearing message ⏳",
    ],
    tagline: "✈️ CHANNELS • GROUPS • BOTS ✈️",
  },
  "whatsapp-room": {
    title: "💬 THE WHATS APP ROOM",
    titleBarStyle: "bg-[#25D366] text-white",
    panelBg: "bg-gradient-to-b from-green-50 via-white to-emerald-50 text-zinc-900",
    accent: "bg-[#25D366] text-white",
    chipColor: "bg-white hover:bg-green-50 text-[#075E54] border border-green-300 shadow-sm",
    icon: ExternalLink,
    quickLinks: [
      { name: "WhatsApp Web", url: "https://web.whatsapp.com", color: "bg-[#25D366]" },
      { name: "Business", url: "https://business.whatsapp.com", color: "bg-green-600" },
      { name: "Status", url: "https://www.whatsapp.com/status", color: "bg-emerald-600" },
    ],
    chips: [
      "💬 Drop your number 📱",
      "Group invite: chat.whatsapp.com/____",
      "Read receipts on 👀",
      "Last seen: just now",
      "Voice note coming 🎙️",
      "Forwarded many times ↪️",
      "Pinned chat 📌",
      "Status update 🟢",
      "End-to-end encrypted 🔐",
      "Add me on WhatsApp 📲",
    ],
    tagline: "💬 CHATS • GROUPS • STATUS 💬",
  },
  "youtube-room": {
    title: "📺 THE YOU TUBE ROOM",
    titleBarStyle: "bg-[#FF0000] text-white",
    panelBg: "bg-gradient-to-b from-red-50 via-white to-zinc-50 text-zinc-900",
    accent: "bg-[#FF0000] text-white",
    chipColor: "bg-white hover:bg-red-50 text-[#FF0000] border border-red-300 shadow-sm",
    icon: Video,
    quickLinks: [
      { name: "YouTube", url: "https://www.youtube.com", color: "bg-[#FF0000]" },
      { name: "Trending", url: "https://www.youtube.com/feed/trending", color: "bg-red-600" },
      { name: "YT Studio", url: "https://studio.youtube.com", color: "bg-rose-600" },
      { name: "Shorts", url: "https://www.youtube.com/shorts", color: "bg-orange-600" },
      { name: "Music", url: "https://music.youtube.com", color: "bg-red-700" },
    ],
    chips: [
      "📺 Subscribe and hit the bell 🔔",
      "Link your channel 👇",
      "Comment of the day 💬",
      "Going live in 5 🔴",
      "Edit suite: Premiere / DaVinci?",
      "First! ⚡",
      "Sponsor of this room ⭐",
      "Drop a like 👍",
      "Watch party 🎬",
      "Algorithm blessed me 🙏",
    ],
    tagline: "📺 SUBSCRIBE • LIKE • SHARE 📺",
  },
};

const SOCIAL_ROOM_IDS = Object.keys(ROOM_THEMES);

const SocialMediaPanel = ({ roomId, onInsertText }) => {
  const [collapsed, setCollapsed] = useState(false);

  if (!SOCIAL_ROOM_IDS.includes(roomId)) return null;

  const theme = ROOM_THEMES[roomId];
  const Icon = theme.icon;

  const openLink = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const insert = (text) => {
    if (onInsertText) {
      onInsertText(text);
    }
  };

  return (
    <div
      className="retro-window mb-2 overflow-hidden"
      data-testid={`social-media-panel-${roomId}`}
    >
      <div className={`flex items-center justify-between px-2 py-1 font-bold text-xs ${theme.titleBarStyle}`}>
        <span className="flex items-center gap-1.5 truncate">
          <Icon className="w-4 h-4 flex-shrink-0" />
          <span className="truncate">{theme.title}</span>
        </span>
        <button
          onClick={() => setCollapsed((c) => !c)}
          className="hover:opacity-75 transition-opacity flex-shrink-0"
          aria-label={collapsed ? "Expand" : "Collapse"}
          data-testid={`social-media-panel-toggle-${roomId}`}
        >
          {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
        </button>
      </div>

      {!collapsed && (
        <div className={`p-2 max-h-[320px] overflow-y-auto retro-scrollbar ${theme.panelBg}`}>
          <div className={`text-center text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded mb-2 ${theme.accent}`}>
            <Sparkles className="w-3 h-3 inline mr-1" />
            {theme.tagline}
          </div>

          {/* Quick external links */}
          <div className="grid grid-cols-2 gap-1 mb-2">
            {theme.quickLinks.map((link, idx) => (
              <button
                key={idx}
                onClick={() => openLink(link.url)}
                className={`${link.color} text-white hover:opacity-90 rounded px-2 py-1 text-[11px] font-bold flex items-center justify-between gap-1 transition-all`}
                data-testid={`social-link-${roomId}-${idx}`}
              >
                <span className="truncate">{link.name}</span>
                <ExternalLink className="w-3 h-3 flex-shrink-0 opacity-80" />
              </button>
            ))}
          </div>

          {/* Insertable phrase chips */}
          <div className="mb-1 flex items-center gap-1 text-[10px] font-bold opacity-80">
            <Hash className="w-3 h-3" />
            <span>Tap to drop in chat</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {theme.chips.map((chip, idx) => (
              <button
                key={idx}
                onClick={() => insert(chip)}
                className={`${theme.chipColor} rounded-full px-2 py-0.5 text-[11px] font-medium transition-all`}
                data-testid={`social-chip-${roomId}-${idx}`}
              >
                {chip}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SocialMediaPanel;
